import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { buildInfantSeatServices, getInfantSeatCount, INFANT_SEAT_PRICE } from '../../utils/infantSeats';
import {
  CHILD_SEAT_PRICE,
  EXTRA_SERVICE_PRICES,
  OVERTIME_RATES,
  calculateAdminExtraServiceTotal,
  cleanNullableText,
  initialExtraServices,
  initialOrderEditForm,
  getEffectiveOrderPrice,
  normalizeVehicleType,
  normalizeManualPriceForUpdate,
} from "./orderEditingConfig";
import {
  EXTENDED_WAITING_SERVICE_TYPE,
  getExtendedWaitingUnitPrice,
} from "../../utils/extendedWaiting";

export default function useAdminOrderEditing({
  apiBaseUrl,
  backendUrl,
  charterPricing,
  extraHoursConfig,
  fetchOrders,
  getErrorMessage,
  refreshStats,
  setLoading,
  transferPricing,
}) {
  const [showOrderEditForm, setShowOrderEditForm] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [orderEditForm, setOrderEditForm] = useState(initialOrderEditForm);
  const [editOrderCustomerPricing, setEditOrderCustomerPricing] = useState(null);
  const [adminEditExtraServices, setAdminEditExtraServices] = useState(initialExtraServices);
  const [adminEditOtherServiceDesc, setAdminEditOtherServiceDesc] = useState("");
  const [adminEditParkingFee, setAdminEditParkingFee] = useState("");
  const [adminEditExtendedWaitingQuantity, setAdminEditExtendedWaitingQuantity] = useState(0);
  const [apiCalculatedPrice, setApiCalculatedPrice] = useState(null);
  const [apiPriceLoading, setApiPriceLoading] = useState(false);

  useEffect(() => {
    if (orderEditForm.service_type === "charter") {
      setAdminEditExtendedWaitingQuantity(0);
    }
  }, [orderEditForm.service_type]);

  const closeOrderEditForm = () => {
    setShowOrderEditForm(false);
    setEditingOrder(null);
    setEditOrderCustomerPricing(null);
  };
  const refreshOrdersAndStats = () => {
    fetchOrders();
    refreshStats?.();
  };

  const handleEditOrder = async (order) => {
    const safeOrder = order || {};
    const safeExtraServices = Array.isArray(safeOrder.extra_services) ? safeOrder.extra_services : [];
    const toNumber = (v, fallback) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : fallback;
    };
    const toText = (v, fallback = "") => (v == null ? fallback : String(v));

    setEditingOrder(safeOrder);
    setOrderEditForm({
      user_id: toText(safeOrder.user_id),
      user_email: toText(safeOrder.user_email),
      customer_name: toText(safeOrder.customer_name),
      phone: toText(safeOrder.phone),
      customer_code: toText(safeOrder.customer_code),
      service_type: toText(safeOrder.service_type, "transfer") || "transfer",
      departure_date: toText(safeOrder.departure_date),
      pickup_time: toText(safeOrder.pickup_time),
      pickup_area: toText(safeOrder.pickup_area),
      dropoff_area: toText(safeOrder.dropoff_area),
      pickup_terminal: toText(safeOrder.pickup_terminal),
      dropoff_terminal: toText(safeOrder.dropoff_terminal),
      pickup_location: toText(safeOrder.pickup_location) || toText(safeOrder.pickup_location_detail),
      pickup_location_detail: toText(safeOrder.pickup_location_detail) || toText(safeOrder.pickup_location),
      destination: toText(safeOrder.destination) || toText(safeOrder.dropoff_location_detail),
      dropoff_location_detail: toText(safeOrder.dropoff_location_detail) || toText(safeOrder.destination),
      flight_number: toText(safeOrder.flight_number),
      passenger_count: toNumber(safeOrder.passenger_count, 1),
      luggage_count: toNumber(safeOrder.luggage_count, 0),
      child_seat_count: toNumber(safeOrder.child_seat_count, 0),
      infant_seat_count: getInfantSeatCount(safeOrder),
      trip_details: toText(safeOrder.trip_details),
      dispatch_note: toText(safeOrder.dispatch_note),
      vehicle_type: toText(safeOrder.vehicle_type, "alphard") || "alphard",
      selected_hours: toNumber(safeOrder.selected_hours, 5),
      end_location: cleanNullableText(safeOrder.end_location),
      end_location_in_base: safeOrder.end_location_in_base === true,
      end_location_other: cleanNullableText(safeOrder.end_location_other),
      price: getEffectiveOrderPrice(safeOrder)?.toString() || "",
      payment_already_received: Boolean(safeOrder.payment_already_received),
      credit_card_payment: Boolean(safeOrder.credit_card_payment),
    });

    const serviceStates = { ...initialExtraServices };
    let otherDesc = "";
    let parkingFee = "";
    let waitingQuantity = 0;
    safeExtraServices.forEach((service) => {
      if (!service || typeof service !== "object") return;
      if (service.service_type === EXTENDED_WAITING_SERVICE_TYPE) {
        waitingQuantity = Math.max(1, Math.min(6, Number(service.quantity) || 1));
      } else if (service.service_type === "other") {
        serviceStates.other = true;
        otherDesc = service.description || "";
      } else if (service.service_type === "parking_fee") {
        serviceStates.parking_fee = true;
        parkingFee = String(Math.max(0, Math.round(Number(service.price) || 0)));
      } else if (service.service_type in serviceStates) {
        serviceStates[service.service_type] = true;
      }
    });

    setAdminEditExtraServices(serviceStates);
    setAdminEditOtherServiceDesc(otherDesc);
    setAdminEditParkingFee(parkingFee);
    setAdminEditExtendedWaitingQuantity(safeOrder.service_type === "transfer" ? waitingQuantity : 0);
    setShowOrderEditForm(true);

    if (safeOrder.user_email) {
      try {
        const response = await axios.get(
          `${apiBaseUrl}/admin/customer-pricing/${encodeURIComponent(safeOrder.user_email)}`
        );
        if (response.data.charter_pricing?.length > 0 || response.data.transfer_pricing?.length > 0) {
          setEditOrderCustomerPricing(response.data);
        } else {
          setEditOrderCustomerPricing(null);
        }
      } catch (error) {
        console.error("获取客户专属定价失败:", error);
        setEditOrderCustomerPricing(null);
      }
    } else {
      setEditOrderCustomerPricing(null);
    }
  };

  const getCharterPriceFromDB = (areaCode, packageHours, vehicleType) => {
    const mappedVehicleType = normalizeVehicleType(vehicleType);

    if (editOrderCustomerPricing?.charter_pricing?.length > 0) {
      const priceField = `price_${mappedVehicleType}`;
      const customerPricing = editOrderCustomerPricing.charter_pricing.find((pricing) =>
        pricing.area_code === areaCode &&
        pricing.package_hours === packageHours
      );
      if (customerPricing && customerPricing[priceField]) {
        return customerPricing[priceField];
      }
    }

    let pricing = charterPricing.find((item) =>
      item.area_code === areaCode &&
      item.package_hours === packageHours &&
      item.vehicle_type === mappedVehicleType &&
      item.enabled !== false &&
      (item.base_area === "tokyo" || !item.base_area)
    );

    if (!pricing) {
      pricing = charterPricing.find((item) =>
        item.area_code === areaCode &&
        item.package_hours === packageHours &&
        item.vehicle_type === mappedVehicleType &&
        item.enabled !== false
      );
    }
    return pricing?.price || 0;
  };

  const getTransferPriceFromDB = (fromArea, toArea, vehicleType) => {
    const mappedVehicleType = normalizeVehicleType(vehicleType);

    if (editOrderCustomerPricing?.transfer_pricing?.length > 0) {
      const priceField = `price_${mappedVehicleType}`;
      let customerPricing = editOrderCustomerPricing.transfer_pricing.find((pricing) =>
        (pricing.from_area === fromArea || pricing.pickup_area === fromArea) &&
        (pricing.to_area === toArea || pricing.dropoff_area === toArea)
      );

      if (!customerPricing) {
        customerPricing = editOrderCustomerPricing.transfer_pricing.find((pricing) =>
          (pricing.from_area === toArea || pricing.pickup_area === toArea) &&
          (pricing.to_area === fromArea || pricing.dropoff_area === fromArea)
        );
      }

      if (customerPricing && customerPricing[priceField]) {
        return customerPricing[priceField];
      }
    }

    const pricing = transferPricing.find((item) =>
      (item.pickup_area === fromArea || item.from_area === fromArea) &&
      (item.dropoff_area === toArea || item.to_area === toArea) &&
      item.vehicle_type === mappedVehicleType &&
      item.enabled !== false
    );
    return pricing?.base_price || pricing?.price || 0;
  };

  const calculateAdminCharterPrice = (
    areaCode,
    selectedHours,
    vehicleType,
    pickupExtraHours = 0,
    endExtraHours = 0,
  ) => {
    const totalBillableHours = pickupExtraHours + selectedHours + endExtraHours;
    const mappedVehicleType = normalizeVehicleType(vehicleType);
    let availablePricings = charterPricing.filter((item) =>
      item.area_code === areaCode &&
      item.vehicle_type === mappedVehicleType &&
      item.enabled !== false &&
      (item.base_area === "tokyo" || !item.base_area)
    ).sort((a, b) => a.package_hours - b.package_hours);

    if (availablePricings.length === 0) {
      availablePricings = charterPricing.filter((item) =>
        item.area_code === areaCode &&
        item.vehicle_type === mappedVehicleType &&
        item.enabled !== false
      ).sort((a, b) => a.package_hours - b.package_hours);
    }

    let packageHours = 10;
    let packagePrice = 0;
    if (availablePricings.length > 0) {
      let selectedPricing = availablePricings.find((pricing) => pricing.package_hours >= totalBillableHours);
      if (!selectedPricing) {
        selectedPricing = availablePricings[availablePricings.length - 1];
      }
      packageHours = selectedPricing.package_hours;
      packagePrice = selectedPricing.price;
    } else {
      for (const packageOption of [5, 8, 10]) {
        if (totalBillableHours <= packageOption) {
          packageHours = packageOption;
          break;
        }
        packageHours = packageOption;
      }
      packagePrice = getCharterPriceFromDB(areaCode, packageHours, vehicleType);
    }

    const overtimeHours = Math.max(0, totalBillableHours - packageHours);
    const overtimeRate = OVERTIME_RATES[mappedVehicleType] || 5000;
    const overtimePrice = overtimeHours * overtimeRate;
    return {
      packageHours,
      packagePrice,
      overtimeHours,
      overtimeRate,
      overtimePrice,
      totalPrice: packagePrice + overtimePrice,
    };
  };

  const getAdminVehiclePrice = (vehicleType) => {
    if (!editingOrder) return 0;

    if (editingOrder.preset_route_id) {
      if (vehicleType === editingOrder.vehicle_type) {
        return editingOrder.base_price || editingOrder.price_snapshot?.base_price || 0;
      }
      return 0;
    }

    if (editingOrder.service_type === "charter") {
      const pickupArea = orderEditForm.pickup_area || editingOrder.pickup_area;
      const dropoffArea = orderEditForm.dropoff_area || editingOrder.dropoff_area || pickupArea;
      const selectedHours = orderEditForm.selected_hours || editingOrder.selected_hours || 5;
      const endLocation = orderEditForm.end_location || "";
      const endLocationInBase = orderEditForm.end_location_in_base;
      const pickupConfig = extraHoursConfig.find((config) =>
        config.config_type === "pickup" && config.area_code === pickupArea
      );
      let endExtraHours = 0;
      if (!endLocationInBase && endLocation) {
        const endConfig = extraHoursConfig.find((config) =>
          config.config_type === "end_location" && config.area_code === endLocation
        );
        endExtraHours = endConfig?.extra_hours || 0;
      }

      const priceInfo = calculateAdminCharterPrice(
        dropoffArea,
        selectedHours,
        vehicleType,
        pickupConfig?.extra_hours || 0,
        endExtraHours,
      );
      if (priceInfo.totalPrice === 0 && vehicleType === editingOrder.vehicle_type) {
        return editingOrder.base_price || editingOrder.price_snapshot?.base_price || 0;
      }
      return priceInfo.totalPrice;
    }

    if (editingOrder.service_type === "transfer") {
      const fromArea = orderEditForm.pickup_area || editingOrder.pickup_area;
      const toArea = orderEditForm.dropoff_area || editingOrder.dropoff_area || fromArea;
      if (fromArea && toArea) {
        const price = getTransferPriceFromDB(fromArea, toArea, vehicleType);
        if (price === 0 && vehicleType === editingOrder.vehicle_type) {
          return editingOrder.base_price || editingOrder.price_snapshot?.base_price || 0;
        }
        return price;
      }
    }

    if (vehicleType === editingOrder.vehicle_type) {
      return editingOrder.base_price || editingOrder.price_snapshot?.base_price || 0;
    }
    return 0;
  };

  const getAdminExtraServiceTotal = useCallback(() => {
    const infantSeatTotal = Number(orderEditForm.infant_seat_count || 0) * INFANT_SEAT_PRICE;
    return infantSeatTotal + calculateAdminExtraServiceTotal(
      adminEditExtraServices,
      adminEditParkingFee,
      orderEditForm.vehicle_type || editingOrder?.vehicle_type,
      orderEditForm.service_type === "transfer" ? adminEditExtendedWaitingQuantity : 0,
    );
  }, [adminEditExtendedWaitingQuantity, adminEditExtraServices, adminEditParkingFee, editingOrder?.vehicle_type, orderEditForm.infant_seat_count, orderEditForm.service_type, orderEditForm.vehicle_type]);

  const getAdminChildSeatPrice = () => {
    const count = Number(orderEditForm.child_seat_count || 0);
    return count > 1 ? (count - 1) * CHILD_SEAT_PRICE : 0;
  };

  const getAdminCalculatedPrice = () => {
    return getAdminVehiclePrice(orderEditForm.vehicle_type) +
      getAdminChildSeatPrice() +
      getAdminExtraServiceTotal();
  };

  const fetchApiCalculatedPrice = useCallback(async () => {
    if (!editingOrder || editingOrder.service_type !== "charter") {
      setApiCalculatedPrice(null);
      return;
    }

    const pickupArea = orderEditForm.pickup_area || editingOrder.pickup_area;
    const dropoffArea = orderEditForm.dropoff_area || editingOrder.dropoff_area || pickupArea;
    const selectedHours = orderEditForm.selected_hours || editingOrder.selected_hours || 5;
    const vehicleType = orderEditForm.vehicle_type || editingOrder.vehicle_type || "alphard";
    if (!pickupArea || !vehicleType) {
      setApiCalculatedPrice(null);
      return;
    }

    setApiPriceLoading(true);
    try {
      const mappedVehicleType = normalizeVehicleType(vehicleType) || "alphard";
      const response = await fetch(`${backendUrl}/api/calculate-price`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          service_type: "charter",
          pickup_area: pickupArea,
          dropoff_area: dropoffArea,
          selected_hours: selectedHours,
          vehicle_type: mappedVehicleType,
          child_seat_count: orderEditForm.child_seat_count || editingOrder.child_seat_count || 0,
          end_location: orderEditForm.end_location || "",
          end_location_in_base: orderEditForm.end_location_in_base !== false,
          end_location_other: orderEditForm.end_location_other || "",
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setApiCalculatedPrice((data.total_price || 0) + getAdminExtraServiceTotal());
      } else {
        console.error("Failed to calculate price via API:", await response.text());
        setApiCalculatedPrice(null);
      }
    } catch (error) {
      console.error("Error calculating price:", error);
      setApiCalculatedPrice(null);
    } finally {
      setApiPriceLoading(false);
    }
  }, [
    backendUrl,
    editingOrder,
    getAdminExtraServiceTotal,
    orderEditForm.child_seat_count,
    orderEditForm.dropoff_area,
    orderEditForm.end_location,
    orderEditForm.end_location_in_base,
    orderEditForm.end_location_other,
    orderEditForm.pickup_area,
    orderEditForm.selected_hours,
    orderEditForm.vehicle_type,
  ]);

  useEffect(() => {
    if (editingOrder && editingOrder.service_type === "charter") {
      const pickupArea = orderEditForm.pickup_area || editingOrder.pickup_area;
      if (pickupArea) {
        fetchApiCalculatedPrice();
      }
    } else {
      setApiCalculatedPrice(null);
    }
  }, [
    editingOrder,
    fetchApiCalculatedPrice,
    orderEditForm.pickup_area,
  ]);

  const handleRecalculateLatestPrice = async () => {
    if (!editingOrder) return;
    if (!window.confirm("确定要使用最新价格表重新计算此订单吗？\n这将更新订单的pricing_version并重新计算价格。")) {
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${apiBaseUrl}/admin/orders/${editingOrder.id}/recalculate-latest-pricing`);
      const result = response.data;
      setOrderEditForm({ ...orderEditForm, price: result.new_price?.toString() || "" });
      setEditingOrder({
        ...editingOrder,
        pricing_version: result.new_pricing_version,
        price: result.new_price,
        base_price: result.new_base_price,
        price_locked: true,
      });
      alert(`价格已使用最新定价表重新计算！\n\n原价: ¥${result.old_price?.toLocaleString()}\n新价: ¥${result.new_price?.toLocaleString()}\n\n版本: v${result.old_pricing_version} → v${result.new_pricing_version}`);
      refreshOrdersAndStats();
    } catch (error) {
      alert(getErrorMessage(error, "重新计算价格失败"));
    } finally {
      setLoading(false);
    }
  };

  const handleRequestSquareRefund = async (payload) => {
    if (!editingOrder?.id) throw new Error("订单信息不完整，请刷新后重试。");
    setLoading(true);
    try {
      const response = await axios.post(
        `${apiBaseUrl}/admin/orders/${editingOrder.id}/square-refund`,
        payload,
      );
      const result = response.data || {};
      setEditingOrder((current) => current ? {
        ...current,
        status: result.status || "refund_processing",
        refund_status: "processing",
        refund_in_progress: true,
        refund_pending_amount: result.refund_pending_amount || payload.refund_amount,
        square_refund_id: result.square_refund_id,
        square_refund_status: result.square_refund_status,
      } : current);
      refreshOrdersAndStats();
      return result;
    } catch (error) {
      throw new Error(getErrorMessage(error, "Square退款请求失败"));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateOrder = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const updateData = { ...orderEditForm };
      delete updateData.infant_seat_count;
      ["payment_already_received", "credit_card_payment"].forEach((field) => {
        if (Boolean(orderEditForm[field]) === Boolean(editingOrder[field])) {
          delete updateData[field];
        } else {
          updateData[field] = Boolean(orderEditForm[field]);
        }
      });
      const manualPrice = normalizeManualPriceForUpdate(updateData.price, editingOrder);
      if (manualPrice === undefined) {
        delete updateData.price;
      } else {
        updateData.price = manualPrice;
      }

      if (editingOrder.preset_route_id) {
        updateData.base_price = getAdminVehiclePrice(orderEditForm.vehicle_type);
      }

      updateData.extra_services = Object.keys(adminEditExtraServices)
        .filter((key) => {
          if (!adminEditExtraServices[key]) return false;
          if (key === "hiace_14_seat_upgrade" && updateData.vehicle_type !== "hiace") return false;
          return true;
        })
        .map((key) => key === "other"
          ? {
              service_type: key,
              price: 0,
              description: adminEditOtherServiceDesc,
              status: "pending_approval",
            }
          : key === "parking_fee"
            ? {
                service_type: key,
                price: Math.max(0, Math.round(Number(adminEditParkingFee) || 0)),
                description: "駐車料金",
                status: "approved",
              }
            : {
              service_type: key,
              price: EXTRA_SERVICE_PRICES[key],
              status: "approved",
            });
      if (updateData.service_type === "transfer" && adminEditExtendedWaitingQuantity > 0) {
        updateData.extra_services.push({
          service_type: EXTENDED_WAITING_SERVICE_TYPE,
          price: getExtendedWaitingUnitPrice(updateData.vehicle_type),
          quantity: adminEditExtendedWaitingQuantity,
          status: "approved",
        });
      }
      updateData.extra_service_total = getAdminExtraServiceTotal();
      updateData.extra_services.push(...buildInfantSeatServices(orderEditForm.infant_seat_count));

      const currentVehicle = normalizeVehicleType(editingOrder.vehicle_type || editingOrder.vehicle_type_v2);
      const nextVehicle = normalizeVehicleType(updateData.vehicle_type);
      if (
        editingOrder.import_source === "gmail_cityride" &&
        currentVehicle !== nextVehicle
      ) {
        const confirmed = window.confirm(
          `这是 CityRide 自动导入订单。原车型为 ${currentVehicle}，将改为 ${nextVehicle}。\n请先核对原始邮件中的车型和车辆台数，确认继续修改吗？`
        );
        if (!confirmed) return;
        updateData.cityride_vehicle_override_confirmed = true;
      }

      const sharedFields = [
        "customer_code", "customer_name", "phone", "departure_date", "pickup_time",
        "pickup_area", "dropoff_area", "pickup_terminal", "dropoff_terminal",
        "pickup_location", "pickup_location_detail", "destination", "dropoff_location_detail",
        "flight_number", "service_type", "selected_hours", "end_location",
        "end_location_in_base", "end_location_other",
      ];
      const sharedChanges = Object.fromEntries(sharedFields
        .filter(key => Object.prototype.hasOwnProperty.call(updateData, key) && String(updateData[key] ?? "") !== String(editingOrder[key] ?? ""))
        .map(key => [key, updateData[key]]));
      const updateWholeGroup = Boolean(editingOrder.booking_group_id && Object.keys(sharedChanges).length && window.confirm(
        `此订单属于${editingOrder.booking_group_size}台车的同组订单。是否将日期、时间、路线等共同资料同步修改到整组？\n选择“取消”则只修改本单。`
      ));
      let groupResult = null;
      let updateResult = null;
      if (updateWholeGroup) {
        groupResult = await axios.put(`${apiBaseUrl}/orders/${editingOrder.id}/group`, sharedChanges);
        const currentVehicleChanges = Object.fromEntries(Object.entries(updateData).filter(([key]) => !sharedFields.includes(key)));
        if (Object.keys(currentVehicleChanges).length) {
          updateResult = await axios.put(`${apiBaseUrl}/orders/${editingOrder.id}`, currentVehicleChanges);
        }
      } else {
        updateResult = await axios.put(`${apiBaseUrl}/orders/${editingOrder.id}`, updateData);
      }
      const paymentPageNeedsRegeneration = Boolean(
        groupResult?.data?.payment_page_needs_regeneration || updateResult?.data?.payment_page_needs_regeneration
      );
      alert(`订单更新成功！${paymentPageNeedsRegeneration ? "\n原未付款链接已作废，请重新生成付款链接。" : ""}`);
      closeOrderEditForm();
      refreshOrdersAndStats();
    } catch (error) {
      alert(getErrorMessage(error, "更新订单失败"));
    } finally {
      setLoading(false);
    }
  };

  return {
    adminEditExtraServices,
    adminEditExtendedWaitingQuantity,
    adminEditOtherServiceDesc,
    adminEditParkingFee,
    apiCalculatedPrice,
    apiPriceLoading,
    closeOrderEditForm,
    editingOrder,
    getAdminCalculatedPrice,
    getAdminChildSeatPrice,
    getAdminExtraServiceTotal,
    getAdminVehiclePrice,
    handleEditOrder,
    handleRecalculateLatestPrice,
    handleRequestSquareRefund,
    handleUpdateOrder,
    orderEditForm,
    setAdminEditExtraServices,
    setAdminEditExtendedWaitingQuantity,
    setAdminEditOtherServiceDesc,
    setAdminEditParkingFee,
    setOrderEditForm,
    showOrderEditForm,
  };
}
